import { useState, useEffect } from 'react'

import {
  Box,
  Button,
  Checkbox,
  FormControl,
  FormControlLabel,
  FormHelperText,
  Grid2,
  TextField,
  Stack,
} from '@mui/material'
import { useTranslation } from 'react-i18next'

import { useProject } from '../../api/project'
import { useUpdateSalesStartSignup } from '../../api/signup'
import { useSignup } from '../../context/signup'
import { useSnackbar } from '../../context/snackbar'
import { useProjectId } from '../../hooks/useProjectId'
import { EmailField as EmailFieldComponent } from '../EmailField'
import { HtmlConnected as Html } from '../html'
import { AddFellow } from './addFellow'
import { Fellows } from './fellows'
import {
  getWindowLocationHref,
  getDocumentReferrer,
  isValid,
  isValidUser,
  isValidCompany,
} from './helper'

export function SalesStartSignupFormComponent() {
  const { t } = useTranslation()

  const { user, mergeUser, nextPage } = useSignup()
  const { showMessage } = useSnackbar()
  const { data: project } = useProject(useProjectId())
  const facebookPixelId = project?.facebookPixelId
  const { mutate: updateSalesStartSignup, isPending: loading } =
    useUpdateSalesStartSignup()

  const [state, setState] = useState(user || {})
  const [location, setLocation] = useState(undefined)
  const [referrer, setReferrer] = useState(undefined)
  const [touched, setTouched] = useState({})

  // Co-applicants added during this form session — there is no GET endpoint to
  // read them back, so the list only lives here.
  const [fellows, setFellows] = useState([])
  const [fellowDialogOpen, setFellowDialogOpen] = useState(false)

  useEffect(() => {
    window.scrollTo(0, 0)
  }, [])

  useEffect(() => {
    setLocation(getWindowLocationHref())
  }, [])

  useEffect(() => {
    setReferrer(getDocumentReferrer())
  }, [])

  const defaultBuyer = project?.allowedBuyers?.[0] || 'PRIVATE'

  const [isOrganization, setOrganization] = useState(defaultBuyer !== 'PRIVATE')

  function handleChange(event) {
    const { name, value } = event.target
    setState({ ...state, [name]: value })
  }

  function handleCheck(event) {
    const { name, checked } = event.target
    setState({ ...state, [name]: checked })
  }

  function handleBlur(event) {
    const { name } = event.target
    setTouched({ ...touched, [name]: true })
  }

  function handleFellowCreated(fellow) {
    setFellows((current) => [...current, fellow])
    setFellowDialogOpen(false)
  }

  function handleFellowDeleted(fellow) {
    setFellows((current) => current.filter((f) => f.id !== fellow.id))
  }

  function handleCompanySubmit(event) {
    event.preventDefault()

    if (loading) return

    if (
      !isValidCompany(state) ||
      !isValid({ acceptPrivacyPolicy: state.acceptPrivacyPolicy })
    ) {
      setTouched({
        firstName: true,
        lastName: true,
        organizationNumber: true,
        organizationName: true,
        email: true,
        phone: true,
        street: true,
        zip: true,
        city: true,
        acceptPrivacyPolicy: true,
      })
      return
    }

    updateSalesStartSignup(
      { ...state, location, referrer },
      {
        onSuccess: (data) => {
          mergeUser({ ...data, signupType: 'SALES_START' })
          nextPage()
        },
        onError: (error) => showMessage(error.message),
      }
    )
  }

  function handleUserSubmit(event) {
    event.preventDefault()

    if (loading) return

    if (
      !isValidUser(state) ||
      !isValid({ acceptPrivacyPolicy: state.acceptPrivacyPolicy })
    ) {
      setTouched({
        firstName: true,
        lastName: true,
        personalNumber: true,
        email: true,
        phone: true,
        street: true,
        zip: true,
        city: true,
        acceptPrivacyPolicy: true,
      })
      return
    }

    updateSalesStartSignup(
      { ...state, location, referrer },
      {
        onSuccess: (data) => {
          mergeUser({ ...data, signupType: 'SALES_START' })
          nextPage()
        },
        onError: (error) => showMessage(error.message),
      }
    )
  }

  return (
    <Stack spacing={3}>
      <Html name="personal-details" />
      <Stack
        spacing={3}
        component="form"
        onSubmit={isOrganization ? handleCompanySubmit : handleUserSubmit}
      >
        <Stack spacing={1}>
          {project?.allowedBuyers?.length > 1 && (
            <FormControlLabel
              label={
                defaultBuyer === 'PRIVATE'
                  ? t('I represent an organization')
                  : t("I'm an individual")
              }
              value={
                defaultBuyer === 'PRIVATE' ? isOrganization : !isOrganization
              }
              onChange={(e) =>
                setOrganization(
                  defaultBuyer === 'PRIVATE'
                    ? e.target.checked
                    : !e.target.checked
                )
              }
              control={
                <Checkbox
                  checked={
                    defaultBuyer === 'PRIVATE'
                      ? isOrganization
                      : !isOrganization
                  }
                />
              }
            />
          )}
          <Grid2 container spacing={2}>
            {isOrganization && (
              <>
                <Grid2
                  size={{
                    xs: 12,
                    sm: 6,
                  }}
                >
                  <TextField
                    fullWidth
                    variant="outlined"
                    onChange={handleChange}
                    onBlur={handleBlur}
                    label={t('Organization name')}
                    name="organizationName"
                    autoComplete="organization"
                    value={state?.organizationName || ''}
                    error={
                      touched.organizationName &&
                      !isValid({ organizationName: state?.organizationName })
                    }
                    helperText={
                      touched.organizationName &&
                      !isValid({ organizationName: state?.organizationName }) &&
                      t('Enter organization name')
                    }
                  />
                </Grid2>
                <Grid2
                  size={{
                    xs: 12,
                    sm: 6,
                  }}
                >
                  <TextField
                    fullWidth
                    variant="outlined"
                    onChange={handleChange}
                    onBlur={handleBlur}
                    label={t('Organization number')}
                    name="organizationNumber"
                    value={state?.organizationNumber || ''}
                    error={
                      touched.organizationNumber &&
                      !isValid({
                        organizationNumber: state?.organizationNumber,
                      })
                    }
                    helperText={
                      touched.organizationNumber &&
                      !isValid({
                        organizationNumber: state?.organizationNumber,
                      }) &&
                      t('Enter organization number')
                    }
                  />
                </Grid2>
              </>
            )}
            <Grid2
              size={{
                xs: 12,
                sm: 6,
              }}
            >
              <TextField
                fullWidth
                variant="outlined"
                onChange={handleChange}
                onBlur={handleBlur}
                label={
                  isOrganization
                    ? t('Contact person first name')
                    : t('First name')
                }
                name="firstName"
                autoComplete="given-name"
                value={state?.firstName || ''}
                error={
                  touched.firstName && !isValid({ firstName: state?.firstName })
                }
                helperText={
                  touched.firstName &&
                  !isValid({ firstName: state?.firstName }) &&
                  t('Enter first name')
                }
              />
            </Grid2>
            <Grid2
              size={{
                xs: 12,
                sm: 6,
              }}
            >
              <TextField
                fullWidth
                variant="outlined"
                onChange={handleChange}
                onBlur={handleBlur}
                label={
                  isOrganization
                    ? t('Contact person last name')
                    : t('Last name')
                }
                name="lastName"
                autoComplete="family-name"
                value={state?.lastName || ''}
                error={
                  touched.lastName && !isValid({ lastName: state?.lastName })
                }
                helperText={
                  touched.lastName &&
                  !isValid({ lastName: state?.lastName }) &&
                  t('Enter last name')
                }
              />
            </Grid2>
            {!isOrganization && (
              <>
                <Grid2
                  size={{
                    xs: 12,
                    sm: 6,
                  }}
                >
                  <TextField
                    fullWidth
                    variant="outlined"
                    onChange={handleChange}
                    onBlur={handleBlur}
                    label={t('Personal number')}
                    placeholder={t('yyyymmddnnnn')}
                    name="personalNumber"
                    type="tel"
                    value={state?.personalNumber || ''}
                    error={
                      touched.personalNumber &&
                      !isValid({ personalNumber: state?.personalNumber })
                    }
                    helperText={
                      touched.personalNumber &&
                      !isValid({ personalNumber: state?.personalNumber }) &&
                      t('Enter social security number (yyyymmddnnnn)')
                    }
                  />
                </Grid2>
                <Grid2
                  size={{
                    sm: 6,
                  }}
                  sx={{ display: { xs: 'none', sm: 'block' } }}
                />
              </>
            )}
            <Grid2
              size={{
                xs: 12,
                sm: 6,
              }}
            >
              <EmailFieldComponent
                email={state?.email || ''}
                touched={touched.email}
                onChange={handleChange}
                onBlur={handleBlur}
              />
            </Grid2>
            <Grid2
              size={{
                xs: 12,
                sm: 6,
              }}
            >
              <TextField
                fullWidth
                variant="outlined"
                onChange={handleChange}
                onBlur={handleBlur}
                label={t('Phone number')}
                name="phone"
                type="tel"
                value={state?.phone || ''}
                error={touched.phone && !isValid({ phone: state?.phone })}
                helperText={
                  touched.phone &&
                  !isValid({ phone: state?.phone }) &&
                  t('Enter phone number')
                }
              />
            </Grid2>
            <Grid2
              size={{
                xs: 12,
                sm: 5,
              }}
            >
              <TextField
                fullWidth
                variant="outlined"
                onChange={handleChange}
                onBlur={handleBlur}
                label={t('Street address')}
                name="street"
                value={state?.street || ''}
                error={touched.street && !isValid({ street: state?.street })}
                helperText={
                  touched.street &&
                  !isValid({ street: state?.street }) &&
                  t('Enter street address')
                }
              />
            </Grid2>
            <Grid2
              size={{
                xs: 12,
                sm: 3,
              }}
            >
              <TextField
                fullWidth
                variant="outlined"
                onChange={handleChange}
                onBlur={handleBlur}
                label={t('Postal code')}
                name="zip"
                type="tel"
                value={state?.zip || ''}
                error={touched.zip && !isValid({ zip: state?.zip })}
                helperText={
                  touched.zip &&
                  !isValid({ zip: state?.zip }) &&
                  t('Enter postal code')
                }
              />
            </Grid2>
            <Grid2
              size={{
                xs: 12,
                sm: 4,
              }}
            >
              <TextField
                fullWidth
                variant="outlined"
                onChange={handleChange}
                onBlur={handleBlur}
                label={t('City')}
                name="city"
                value={state?.city || ''}
                error={touched.city && !isValid({ city: state?.city })}
                helperText={
                  touched.city &&
                  !isValid({ city: state?.city }) &&
                  t('Enter city')
                }
              />
            </Grid2>
          </Grid2>
        </Stack>
        {project?.allowFellows && (
          <>
            <AddFellow
              open={fellowDialogOpen}
              onOpen={() => setFellowDialogOpen(true)}
              onClose={() => setFellowDialogOpen(false)}
              onCreated={handleFellowCreated}
            />
            <Fellows fellows={fellows} onDeleted={handleFellowDeleted} />
          </>
        )}
        <Stack>
          {project?.membership && (
            <FormControlLabel
              name="membership"
              control={
                <Checkbox
                  name="membership"
                  checked={state?.membership}
                  onChange={handleCheck}
                />
              }
              label={<Html name="membership" />}
            />
          )}
          {facebookPixelId && (
            <FormControl component="fieldset">
              <FormControlLabel
                control={
                  <Checkbox
                    name="acceptTracking"
                    checked={state?.acceptTracking}
                    onChange={handleCheck}
                  />
                }
                label={t('I accept tracking for marketing purposes (optional)')}
              />
            </FormControl>
          )}
          <FormControl
            required
            error={
              touched.acceptPrivacyPolicy &&
              !isValid({ acceptPrivacyPolicy: state?.acceptPrivacyPolicy })
            }
            component="fieldset"
          >
            <FormControlLabel
              control={
                <Checkbox
                  name="acceptPrivacyPolicy"
                  checked={state?.acceptPrivacyPolicy}
                  onChange={handleCheck}
                  onBlur={handleBlur}
                />
              }
              label={<Html name="privacy-policy-checkbox-label" />}
            />
            {touched.acceptPrivacyPolicy &&
              !isValid({
                acceptPrivacyPolicy: state?.acceptPrivacyPolicy,
              }) && (
                <FormHelperText>
                  {t('Please accept our personal data policy')}
                </FormHelperText>
              )}
          </FormControl>
        </Stack>
        <Box>
          <Button
            variant="contained"
            type="submit"
            loading={loading}
            onSubmit={isOrganization ? handleCompanySubmit : handleUserSubmit}
          >
            {t('Continue')}
          </Button>
        </Box>
      </Stack>
    </Stack>
  )
}
